import React from 'react';
import {
  AppstoreOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';

export interface ProductStatusInfo {
  color: 'success' | 'error' | 'warning' | 'info' | 'default';
  text: string;
  icon: React.ReactElement;
  isTag: boolean;
}

interface ProductStatusParams {
  isActive: boolean;
  paymentStatus?: string;
  isFreeProduct: boolean;
  trialEnd?: string | null;
  mustEnforceForm: boolean;
  formStatus?: string | null;
  requiresApi: boolean;
  hasApiConnected: boolean;
}

export const getProductStatusInfo = (params: ProductStatusParams): ProductStatusInfo => {
  const {
    isActive,
    paymentStatus,
    isFreeProduct,
    trialEnd,
    mustEnforceForm,
    formStatus,
    requiresApi,
    hasApiConnected,
  } = params;

  const isInTrial = trialEnd && new Date(trialEnd) > new Date();
  const isPaymentBlocking = !isFreeProduct && !isInTrial && paymentStatus && !['paid', 'succeeded'].includes(paymentStatus);
  const isFormBlocking = mustEnforceForm && formStatus !== 'approved';
  const isOAuthBlocking = requiresApi && !hasApiConnected;

  // Se está ativo, mostrar como ativo
  if (isActive) {
    return {
      color: 'success',
      text: 'Ativo',
      icon: <AppstoreOutlined />,
      isTag: true,
    };
  }

  // PRIORIDADE 1: Pagamento vencido (VERMELHO)
  if (paymentStatus === 'overdue') {
    return {
      color: 'error',
      text: 'Pagamento vencido — regularize para continuar.',
      icon: <WarningOutlined />,
      isTag: false,
    };
  }

  // PRIORIDADE 2: Pagamento pendente (VERMELHO)
  if (isPaymentBlocking) {
    return {
      color: 'error',
      text: 'Pagamento pendente. Aguarde confirmação para ativar.',
      icon: <ExclamationCircleOutlined />,
      isTag: false,
    };
  }

  // PRIORIDADE 3: Formulário pendente
  if (mustEnforceForm && (!formStatus || formStatus === 'pending_submission')) {
    return {
      color: 'warning',
      text: 'Configure o formulário para ativar.',
      icon: <ExclamationCircleOutlined />,
      isTag: false,
    };
  }

  // PRIORIDADE 4: Formulário em análise
  if (mustEnforceForm && formStatus === 'submitted') {
    return {
      color: 'info',
      text: 'Formulário em análise. Aguarde aprovação para ativar.',
      icon: <SyncOutlined />,
      isTag: false,
    };
  }

  // PRIORIDADE 5: Formulário reprovado
  if (mustEnforceForm && formStatus === 'rejected') {
    return {
      color: 'error',
      text: 'Formulário reprovado. Corrija para ativar.',
      icon: <CloseCircleOutlined />,
      isTag: false,
    };
  }

  // PRIORIDADE 6: API não configurada
  if (isOAuthBlocking) {
    return {
      color: 'warning',
      text: 'Conecte a API para ativar o produto.',
      icon: <ExclamationCircleOutlined />,
      isTag: false,
    };
  }

  // Tudo configurado: Pronto para ativação
  if (!isFormBlocking && !isOAuthBlocking && !isPaymentBlocking) {
    return {
      color: 'success',
      text: 'Tudo pronto! Ative o produto para começar.',
      icon: <CheckCircleOutlined />,
      isTag: false,
    };
  }

  // Produto inativo sem bloqueios
  return {
    color: 'default',
    text: 'Inativo',
    icon: <AppstoreOutlined />,
    isTag: true,
  };
};

export const hasCredentialsRequirement = (source: any) => {
  if (!source) return false;
  if (Array.isArray(source)) return source.length > 0;
  if (typeof source === 'object') return Object.keys(source).length > 0;
  return Boolean(source);
};

export const computeApiRequirement = (product: any) => {
  const requiresOAuth = Boolean((product.product as any)?.requires_oauth);
  const oauthRequirement = hasCredentialsRequirement((product as any).oauth_credentials) ||
    hasCredentialsRequirement((product.product as any)?.oauth_credentials);
  
  const hasWorkflow = Boolean(product.cloned_workflow_id || product.custom_workflow_id);
  
  const requiresApi = requiresOAuth || oauthRequirement || hasWorkflow;
  const hasApiConnected = Boolean(
    (product as any).has_oauth_credentials_configured ||
    (product as any).has_api_credentials_configured ||
    (product as any).api_connected
  );

  return { requiresApi, hasApiConnected };
};
